import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function TygerbergPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    if (typeof document === 'undefined') return;

    try {
      document.title = 'Tygerberg Painters Cape Town | Residential & Commercial Painting Services';

      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Professional painting services in Tygerberg, Cape Town. Expert residential and commercial painting for homes, businesses, and developments. Quality finishes and reliable service.');
      }

      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute('content', 'Tygerberg painters, residential painting Cape Town, commercial painting services');
      }

      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/tygerberg`);
      }
    } catch (err) {
      console.error('Error setting up meta tags:', err);
    }
  }, []);

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-32 min-h-[600px]">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/795c43e126d6c6c6b2e711420dad8054.jpg"
            alt="Professional Painting Services in Tygerberg"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Professional <span className="text-yellow-400">Tygerberg Painters</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Expert residential and commercial painting services for Tygerberg properties with quality finishes and reliable professional service.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="bg-yellow-400 text-black px-8 py-4 rounded-lg text-xl font-bold hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer">
                Get Free Quote
              </Link>
              <Link to="/services" className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200">
                Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-6 text-gray-900">
              Comprehensive Painting Services for Tygerberg's Diverse Properties
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
              <p>
                Tygerberg represents a vibrant and diverse area of Cape Town, encompassing residential neighborhoods, commercial developments, medical facilities, educational institutions, and mixed-use properties. This dynamic region requires painting services that can adapt to various property types, understand different client needs, and deliver consistent quality across residential, commercial, and institutional projects throughout the Tygerberg area.
              </p>
              <p>
                Our comprehensive painting services in Tygerberg cater to homeowners, business owners, property managers, and institutional clients who value quality workmanship, professional service, and reliable results. We understand the diverse nature of Tygerberg properties and provide tailored solutions that meet specific requirements, whether painting a family home, commercial office, medical facility, or multi-unit residential development.
              </p>
              <p>
                Working in Tygerberg requires versatility, professionalism, and understanding of both residential and commercial painting standards. Our experienced team has completed numerous projects throughout this area, from suburban homes and townhouse complexes to business premises and institutional facilities, earning a reputation for quality, reliability, and customer satisfaction across all property types and project sizes.
              </p>
              <p>
                From interior and exterior residential painting to commercial property maintenance and new development projects, we provide comprehensive painting solutions that enhance property aesthetics, protect valuable investments, and deliver lasting results. Our commitment to excellence ensures every Tygerberg property receives professional care, quality materials, and expert workmanship that meets or exceeds expectations regardless of project scope or complexity.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Our Tygerberg Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Versatile painting solutions for residential, commercial, and institutional properties
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-home-4-line',
                title: 'Residential Home Painting',
                description: 'Professional interior and exterior painting for Tygerberg homes including houses, townhouses, and apartments with quality finishes and attention to detail.',
                features: ['Interior painting', 'Exterior painting', 'Color consultation', 'Quality materials', 'Clean workmanship', 'Timely completion']
              },
              {
                icon: 'ri-building-line',
                title: 'Commercial Property Painting',
                description: 'Expert painting for offices, retail spaces, medical facilities, and commercial buildings with minimal business disruption and professional results.',
                features: ['Office painting', 'Retail spaces', 'Medical facilities', 'Flexible scheduling', 'Professional finish', 'Business-friendly']
              },
              {
                icon: 'ri-community-line',
                title: 'Multi-Unit Developments',
                description: 'Comprehensive painting for apartment complexes, townhouse developments, and residential estates with coordinated scheduling and consistent quality.',
                features: ['Complex coordination', 'Consistent quality', 'Resident liaison', 'Efficient execution', 'Body corporate compliance', 'Project management']
              },
              {
                icon: 'ri-paint-brush-line',
                title: 'New Construction Painting',
                description: 'Professional painting for new builds and developments including residential and commercial properties with quality assurance and timeline adherence.',
                features: ['New build expertise', 'Developer partnerships', 'Quality control', 'Timeline compliance', 'Specification adherence', 'Final inspections']
              },
              {
                icon: 'ri-shield-star-line',
                title: 'Exterior Protection Systems',
                description: 'Weather-resistant exterior painting designed for Cape Town\'s climate with durable coatings that protect and beautify Tygerberg properties.',
                features: ['Weather protection', 'UV resistance', 'Moisture barriers', 'Long-lasting finishes', 'Color retention', 'Durability guarantee']
              },
              {
                icon: 'ri-tools-line',
                title: 'Maintenance & Restoration',
                description: 'Ongoing maintenance programs and restoration services for existing properties with preventive care, repairs, and protective coatings.',
                features: ['Regular maintenance', 'Surface restoration', 'Preventive care', 'Repair services', 'Touch-up programs', 'Long-term protection']
              }
            ].map((service, index) => (
              <div key={index} className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-all cursor-pointer">
                <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${service.icon} text-3xl text-black`}></i>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">{service.title}</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <i className="ri-check-line text-yellow-400 text-xl mr-2 flex-shrink-0"></i>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Understanding Tygerberg's Diverse Painting Requirements
          </h2>
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Residential Diversity</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Tygerberg encompasses a wide range of residential properties from established family homes and modern townhouse complexes to apartment buildings and new developments. This diversity requires flexible painting approaches that can adapt to different architectural styles, property ages, and homeowner preferences while maintaining consistent quality standards across all residential projects.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Our residential painting expertise includes understanding various property types, working with different surface materials, and providing appropriate solutions for each unique situation. We deliver professional results whether painting a single-family home, coordinating a multi-unit complex, or completing a new residential development throughout Tygerberg.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Commercial Requirements</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Commercial properties in Tygerberg include medical facilities, office buildings, retail spaces, and institutional properties that require professional painting services with minimal operational disruption. Our commercial painting expertise includes flexible scheduling, efficient execution, and understanding of business needs to ensure projects are completed with minimal impact on daily operations.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We work closely with business owners, facility managers, and property administrators to coordinate painting projects around operational schedules, maintain professional appearances, and deliver results that enhance commercial property appeal while respecting business continuity requirements throughout the Tygerberg commercial sector.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Climate Adaptation</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Cape Town's climate brings strong winds, intense UV exposure, seasonal rainfall, and temperature variations that affect painted surfaces. Our painting systems utilize weather-resistant coatings, UV-stable pigments, and proper surface preparation techniques that ensure long-lasting protection and color retention for Tygerberg properties despite challenging environmental conditions.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We select appropriate materials and application methods based on property exposure, surface types, and environmental factors to maximize paint performance and durability. Our climate-specific approach ensures Tygerberg properties receive optimal protection and maintain beautiful appearances for years with minimal maintenance requirements.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Project Coordination</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Successful painting projects require effective coordination, clear communication, and professional project management. We maintain transparent communication with clients, coordinate with other trades when necessary, and manage projects efficiently to ensure timely completion within agreed budgets and quality standards.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Our project management approach includes detailed planning, regular progress updates, and proactive problem-solving that keeps projects on track and clients informed. We respect property owners, residents, and business operations while delivering professional results that meet or exceed expectations throughout Tygerberg.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Our Tygerberg Painting Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach ensuring exceptional results for all property types
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Initial Consultation',
                description: 'Comprehensive property assessment with detailed discussion of your requirements, preferences, and project expectations for residential or commercial properties.'
              },
              {
                step: '02',
                title: 'Planning & Quotation',
                description: 'Detailed project planning including material selection, color consultation, timeline development, and transparent quotation with itemized costs and no hidden fees.'
              },
              {
                step: '03',
                title: 'Preparation Phase',
                description: 'Thorough surface preparation including cleaning, repairs, protection of surrounding areas, and proper priming to ensure optimal paint adhesion and lasting results.'
              },
              {
                step: '04',
                title: 'Professional Application',
                description: 'Expert painting by experienced craftsmen using quality materials, proven techniques, and attention to detail that delivers exceptional finishes and durability.'
              },
              {
                step: '05',
                title: 'Quality Control',
                description: 'Rigorous quality inspections throughout the project ensuring consistent standards, proper coverage, and flawless finishes that meet our exacting requirements.'
              },
              {
                step: '06',
                title: 'Final Inspection',
                description: 'Comprehensive final walkthrough with detailed touch-ups, cleanup, and client approval ensuring complete satisfaction with the finished project.'
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="text-5xl font-bold text-yellow-400 mb-4">{item.step}</div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">{item.title}</h3>
                <p className="text-gray-700 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Why Tygerberg Property Owners Choose Us
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-shield-check-line',
                title: 'Quality Assurance',
                description: 'Commitment to exceptional quality using premium materials, experienced craftsmen, and rigorous quality control processes that ensure lasting results for all Tygerberg properties.'
              },
              {
                icon: 'ri-team-line',
                title: 'Experienced Team',
                description: 'Skilled professionals with extensive experience in both residential and commercial painting, understanding diverse property requirements and delivering consistent quality across all project types.'
              },
              {
                icon: 'ri-time-line',
                title: 'Reliable Service',
                description: 'Dependable, punctual service with efficient project execution, clear communication, and respect for your time and property throughout the painting process.'
              },
              {
                icon: 'ri-customer-service-line',
                title: 'Professional Approach',
                description: 'Courteous, professional service from initial consultation through project completion with attention to your needs, concerns, and satisfaction at every stage.'
              },
              {
                icon: 'ri-price-tag-3-line',
                title: 'Competitive Pricing',
                description: 'Fair, transparent pricing with detailed quotations, no hidden costs, and excellent value for quality workmanship and materials that protect your investment.'
              },
              {
                icon: 'ri-hand-heart-line',
                title: 'Customer Satisfaction',
                description: 'Your complete satisfaction is our priority. We stand behind our work with comprehensive guarantees and remain committed to delivering results that exceed expectations.'
              }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${item.icon} text-3xl text-black`}></i>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">{item.title}</h3>
                <p className="text-gray-700 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Projects Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Recent Tygerberg Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcase of our diverse painting projects throughout Tygerberg
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: 'Family Home Transformation',
                location: 'Tygerberg Residential Area',
                description: 'Complete interior and exterior painting for a 3-bedroom family home including modern color scheme, quality finishes, and weather-resistant exterior coatings that enhanced the property\'s curb appeal and protected the investment.',
                image: 'https://readdy.ai/api/search-image?query=Modern%20family%20home%20in%20Tygerberg%20Cape%20Town%20with%20fresh%20paint%2C%20suburban%20setting%2C%20neat%20exterior%2C%20professional%20finish%2C%20residential%20neighborhood%2C%20quality%20workmanship&width=800&height=600&seq=tygerberg-project-001&orientation=landscape'
              },
              {
                title: 'Medical Facility Painting',
                location: 'Tygerberg Medical District',
                description: 'Professional painting for a medical office facility including hygienic finishes, coordinated scheduling to minimize patient disruption, and durable coatings suitable for high-traffic healthcare environments.',
                image: 'https://readdy.ai/api/search-image?query=Medical%20office%20building%20in%20Tygerberg%20Cape%20Town%20with%20professional%20paint%2C%20clean%20exterior%2C%20healthcare%20facility%2C%20modern%20finish%2C%20professional%20appearance&width=800&height=600&seq=tygerberg-project-002&orientation=landscape'
              },
              {
                title: 'Townhouse Complex Project',
                location: 'Tygerberg Development',
                description: 'Comprehensive painting for a 12-unit townhouse complex including coordinated exterior colors, individual interior customization, and efficient project management that ensured consistent quality across all units.',
                image: 'https://readdy.ai/api/search-image?query=Townhouse%20complex%20in%20Tygerberg%20Cape%20Town%20with%20fresh%20paint%2C%20modern%20residential%20development%2C%20coordinated%20colors%2C%20professional%20finish%2C%20suburban%20setting&width=800&height=600&seq=tygerberg-project-003&orientation=landscape'
              },
              {
                title: 'Commercial Office Renovation',
                location: 'Tygerberg Business District',
                description: 'Professional interior painting for a commercial office space including modern color scheme, minimal business disruption through after-hours scheduling, and quality finishes that created a professional work environment.',
                image: 'https://readdy.ai/api/search-image?query=Commercial%20office%20building%20in%20Tygerberg%20Cape%20Town%20with%20professional%20paint%2C%20modern%20exterior%2C%20business%20property%2C%20quality%20finish%2C%20professional%20appearance&width=800&height=600&seq=tygerberg-project-004&orientation=landscape'
              }
            ].map((project, index) => (
              <div key={index} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all">
                <div className="w-full h-80">
                  <img src={project.image} alt={project.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-8">
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <i className="ri-map-pin-line mr-2"></i>
                    {project.location}
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900">{project.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            What Tygerberg Clients Say
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'John & Mary Stevens',
                location: 'Tygerberg Homeowner',
                text: 'Excellent service from start to finish. The team was professional, punctual, and delivered beautiful results. Our home looks fantastic and the quality of work is outstanding. Highly recommend for any painting project!'
              },
              {
                name: 'Dr. Peter Jacobs',
                location: 'Medical Practice Owner',
                text: 'Professional painting service with minimal disruption to our practice. The team worked efficiently, maintained cleanliness, and delivered quality results. Perfect for commercial properties requiring reliable service.'
              },
              {
                name: 'Sarah Williams',
                location: 'Property Manager',
                text: 'Managed our townhouse complex painting project excellently. Great communication, consistent quality across all units, and completed on schedule. Reliable partner for multi-unit developments in Tygerberg.'
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-xl"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed italic">&quot;{testimonial.text}&quot;</p>
                <div>
                  <p className="font-bold text-gray-900">{testimonial.name}</p>
                  <p className="text-gray-600">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-black mb-6">
            Ready to Transform Your Tygerberg Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact us today for a free consultation and discover how our professional painting services can enhance your residential or commercial property.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer">
              Get Free Quote
            </Link>
            <a href="tel:+27123456789" className="bg-white text-black px-8 py-4 rounded-lg text-xl font-bold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
              Call Now
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}